import QuickNewsBlueIcon from "@assets/header/newIcons/quick-news-logo.svg";
import QuickNewsIcon from "@assets/quick-news/QnIcon.svg";
import QuickNewsBack from "@assets/quick-news/quick-news-back.svg";
import { QUICKNEWS_FLAG } from "@helper/getEnvVariables";
import { loadLottieAnimation, LottieConfig } from "@helper/lottieHelper";
import { isClientSide } from "@helper/utils";
import useCatfishObserver from "@hooks/useCatfishObserver";
import Cookies from "js-cookie";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

/** -----------------------
 *  Components
 * ----------------------- */
export function QnHeaderIcon({ variant }: { variant?: "blue" | "white" }) {
  const ForNewMasthead = variant === "blue";
  const Icon = ForNewMasthead ? QuickNewsBlueIcon : QuickNewsIcon;
  const buttonStyle = ForNewMasthead
    ? "flex w-[36px] h-[40px]"
    : "w-6 h-6 pt-1 pl-2 border-l-[0.5px] border-grey-1000";
  return (
    <div className={buttonStyle}>
      <Link
        to="/quick-news?ref=top-menu-quick-news"
        reloadDocument
        data-testid="QuickNewsHeaderIcon"
        onClick={() => {
          Cookies.set(
            "qnRedirectUrl",
            encodeURIComponent(window.location.href),
          );
        }}
        className="m-auto"
      >
        <img src={Icon} alt="quick-news-icon" width="10" height="18" />
      </Link>
    </div>
  );
}

/** -----------------------
 *  Hooks
 * ----------------------- */
export const useViewportHeight = () => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!isClientSide) return;

    setHeight(window.innerHeight);

    const onResize = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return height;
};

/**
 * Hide/show floating button based on scroll direction & threshold
 */
export const useScrollVisibility = (threshold: number) => {
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const updateVisibility = () => {
      const currentY = window.scrollY;

      if (currentY === 0) {
        setVisible(true);
      } else if (currentY > lastScrollY.current && currentY >= threshold) {
        setVisible(false);
      } else if (currentY < lastScrollY.current && currentY < threshold) {
        setVisible(true);
      }

      lastScrollY.current = currentY;
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updateVisibility);
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [threshold]);

  return visible;
};

export function QnFloatButton({
  refCode,
  isCatfishEnabled,
}: {
  refCode: string;
  isCatfishEnabled: boolean | undefined;
}) {
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    if (!isClientSide) return;
    setIsMounted(true);
    setIsMobile(window.innerWidth <= 768);

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const shouldRender = QUICKNEWS_FLAG === "1" && isMounted;
  const { isCatfishAvailable } = useCatfishObserver({
    isCatfishEnabled,
    isMobile,
  });

  const canvasId = "lightning-canvas";

  const lightningConfig = useMemo<LottieConfig>(
    () => ({
      animationPath: "quick-news/lightning.lottie",
      canvasId,
      dimensions: { width: 24, height: 38 },
    }),
    [canvasId],
  );

  useEffect(() => {
    if (!isMounted) return;

    const instance = loadLottieAnimation(lightningConfig);
    return () => instance?.destroy();
  }, [isMounted, lightningConfig]);

  // Return early AFTER hooks run
  if (!shouldRender) return null;

  return (
    <div
      className={twMerge(
        "quick-news-float fixed right-4 lg:right-[calc(-600px+(0.5*100svw))] z-50 transition-all duration-1000",
        "opacity-100 translate-y-0",
        isCatfishAvailable ? "bottom-[160px]" : "bottom-[110px] md:bottom-20",
      )}
    >
      <Link
        to={`/quick-news?ref=${refCode}`}
        reloadDocument
        className="w-[70px] lg:w-[125px] h-[35px] lg:h-[44px] flex items-center justify-center rounded-tr-[5px] rounded-bl-[5px] [clip-path:polygon(10px_0,100%_0,100%_calc(100%-10px),calc(100%-10px)_100%,0_100%,0_10px)] bg-[linear-gradient(90deg,_#841DDA_0.09%,_#5A31B2_22.54%,_#195288_57.04%)] drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)]"
        onClick={() => {
          Cookies.set(
            "qnRedirectUrl",
            encodeURIComponent(window.location.href),
          );
        }}
      >
        <div className="px-1 relative flex items-center justify-center mb-2">
          <canvas id={canvasId} className="w-[25px]" />
        </div>

        <div className="hidden lg:inline text-white-100 font-regular text-xs leading-[150%] pr-1">
          {"Sekilas Berita"}
        </div>
      </Link>
    </div>
  );
}

export function BackToQuickNews() {
  useEffect(() => {
    const stickyBars = document.querySelectorAll<HTMLDivElement>(
      ".v_stickyArticleNow",
    );

    if (stickyBars.length === 0) return;

    const previousTopValues = Array.from(stickyBars, (stickyBar) => {
      const previousTop = stickyBar.style.top;
      stickyBar.style.top = "37px";
      return previousTop;
    });

    return () => {
      stickyBars.forEach((stickyBar, index) => {
        stickyBar.style.top = previousTopValues[index] || "";
      });
    };
  }, []);

  return (
    <div className="flex flex-col font-primary text-blue-400 h-auto w-full my-0 mx-auto sticky top-0 z-[51]">
      <div className="flex flex-col bg-white-100">
        <div className="lg:max-w-screen-lg xl:max-w-screen-cxl w-full mx-auto h-[37px] lg:h-auto py-1 lg:py-2 px-xs lg:px-xxs">
          <button
            aria-label="Return to Sekilas Berita"
            className="flex items-center gap-1 w-max"
            onClick={() => {
              window.location.href =
                "/quick-news?ref=article-back-to-quick-news";
            }}
          >
            <div className="w-[20px]">
              <img
                src={QuickNewsBack}
                alt="back-to-quick-news"
                className="inline mr-2 w-[14px] h-[20px] sm:w-[16px] sm:h-[26px]"
              />
            </div>
            <div className="text-xxxs leading-2xl font-semibold ">
              Return to <br />
              Sekilas Berita
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
